import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useParams} from 'react-router-dom'
import {
  Button,
  MenuItem,
  OutlinedInput,
  Select,
  makeStyles,
} from '@material-ui/core'
import {Locale} from 'components/atoms/TranslationSelect'
import Header from 'pages/HealthCenter/Report/Elements/Header'
import HealthCenterMajorIndicators from 'pages/HealthCenter/Report/HealthCenterMajorIndicators'
import HealthCenterMajorIndicatorsV1 from 'pages/HealthCenter/ReportV1/HealthCenterMajorIndicators'
import Logo from 'pages/HealthCenter/Report/Elements/logo.svg'
import IconDownload from 'pages/HealthCenter/Report/Elements/IconDownload.svg'
import HealthCenterReportStyle from 'pages/HealthCenter/Report/Style'
import HealthCenterHeartRateAnalysisPage from 'pages/HealthCenter/Report/HealthCenterHeartRateAnalysis'
import HealthCenterHeartRateAnalysisPageV1 from 'pages/HealthCenter/ReportV1/HealthCenterHeartRateAnalysis'
import BrainFunctionBasic from 'pages/HealthCenter/Report/BrainFunctionBasic'
import BrainFunctionBasicV1 from 'pages/HealthCenter/ReportV1/BrainFunctionBasic'
import BrainFunctionalConnectivity from 'pages/HealthCenter/Report/BrainFunctionalConnectivity'
import BrainFunctionalConnectivityV1 from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity'
import AIDementiaPrediction from 'pages/HealthCenter/Report/AIDementiaPrediction'
import AIDementiaPredictionV1 from 'pages/HealthCenter/ReportV1/AIDementiaPrediction'
import {
  FiveFeaturesScore,
  HealthReport,
  HealthyCheckupReport,
  createHealthCheckupPDFReport,
  getDataHealthCheckupReportApi,
  getDataHealthReportApi,
  getHealthProductsApi,
} from 'api/healthCareApi'
import RouteConstant from 'constants/RouteConstant'
import {GenderValue, toGenderString} from 'constants/CommonConstant'
import {LIST_HEALTH_PRODUCT_TAB} from 'constants/AnalysisConstant'
import BrainFunctionMappingPageV1 from 'pages/HealthCenter/ReportV1/BrainFunctionMapping'
import WrapPrintPage from 'pages/HealthCenter/Report/Elements/WrapPrintPage'
import useLanguageReport from 'hooks/useLanguageReport'
import HeaderMultiLangReport from 'pages/HealthCenter/Report/Elements/HeaderMultiLang'
import {
  ResBrainConnectivity,
  ResHrvAnalysis,
  ResMCI,
  ResMCIV2,
} from 'api/languageApi'
import {Lobe, Roi} from 'api/analysisApi'
import {getAccessToken} from 'api/storageApi'
import {downloadFileFromWsUrl} from 'helpers/fileHelper'
import {TYPE_INDICATOR_PRODUCT} from 'pages/ProductsPages/constant'
import useLanguage from 'features/languageReport/useLanguage'
import {SCORE_HEALTH_REDUCE} from 'constants/HealthCenterConstant'
import useProductManagement from 'features/productManagement/useProductManagement'
import AIDementiaPredictionV2 from '../ReportV2/AIDementiaPrediction'

interface TabType {
  index: number
  name: string
}
const useStyles = makeStyles(HealthCenterReportStyle)

function normalizeScore(score: number): number {
  if (score < SCORE_HEALTH_REDUCE.low) return SCORE_HEALTH_REDUCE.low
  if (score > SCORE_HEALTH_REDUCE.high) return SCORE_HEALTH_REDUCE.high
  return score
}

function normalizeFiveFeatures(data: FiveFeaturesScore): FiveFeaturesScore {
  return {
    sadness: normalizeScore(data.sadness),
    tension: normalizeScore(data.tension),
    stress: normalizeScore(data.stress),
    dominant_frequency: {
      score: normalizeScore(data.dominant_frequency.score),
      value: data.dominant_frequency.value,
    },
    inattention: {
      score: normalizeScore(data.inattention.score),
      value: data.inattention.value,
    },
  }
}

function normalizeBrainConnectivityBetweenLobesScore(
  score: {
    vertical: {left: number; right: number}
    horizontal: number
  } | null,
) {
  if (!score) return null

  return {
    vertical: {
      left: normalizeScore(score.vertical.left),
      right: normalizeScore(score.vertical.right),
    },
    horizontal: normalizeScore(score.horizontal),
  }
}

function normalizeBrainConnectivityByLobeScore(
  score: {
    frontal: {left: number; right: number}
    pariocci: {left: number; right: number}
  } | null,
) {
  if (!score) return null

  return {
    frontal: {
      left: normalizeScore(score.frontal.left),
      right: normalizeScore(score.frontal.right),
    },
    pariocci: {
      left: normalizeScore(score.pariocci.left),
      right: normalizeScore(score.pariocci.right),
    },
  }
}

function normalizeBrainMappingBasicByLobeScore(score: Lobe) {
  return {
    Frontal_LH: normalizeScore(Number(score.Frontal_LH)),
    Frontal_RH: normalizeScore(Number(score.Frontal_RH)),
    Occipital_LH: normalizeScore(Number(score.Occipital_LH)),
    Occipital_RH: normalizeScore(Number(score.Occipital_RH)),
    Parietal_LH: normalizeScore(Number(score.Parietal_LH)),
    Parietal_RH: normalizeScore(Number(score.Parietal_RH)),
    Temporal_LH: normalizeScore(Number(score.Temporal_LH)),
    Temporal_RH: normalizeScore(Number(score.Temporal_RH)),
  }
}

function normalizeBrainMappingAdvancedByRoiScore(score: Roi) {
  return {
    Rostral_Middle_Frontal_LH: normalizeScore(
      Number(score.Rostral_Middle_Frontal_LH),
    ),
    Paracentral_RH: normalizeScore(Number(score.Paracentral_RH)),
    Superior_Parietal_RH: normalizeScore(Number(score.Superior_Parietal_RH)),
    Inferior_Parietal_RH: normalizeScore(Number(score.Inferior_Parietal_RH)),
    Insula_LH: normalizeScore(Number(score.Insula_LH)),
    Posterior_Cingulate_LH: normalizeScore(
      Number(score.Posterior_Cingulate_LH),
    ),
    Superior_Temporal_LH: normalizeScore(Number(score.Superior_Temporal_LH)),
    Temporal_Pole_LH: normalizeScore(Number(score.Temporal_Pole_LH)),
    Inferior_Temporal_LH: normalizeScore(Number(score.Inferior_Temporal_LH)),
    Superior_Frontal_LH: normalizeScore(Number(score.Superior_Frontal_LH)),
    Supramarginal_LH: normalizeScore(Number(score.Supramarginal_LH)),
    Pars_Triangularis_RH: normalizeScore(Number(score.Pars_Triangularis_RH)),
    Banks_of_Superior_Temporal_Sulcus_RH: normalizeScore(
      Number(score.Banks_of_Superior_Temporal_Sulcus_RH),
    ),
    Lateral_Orbitofrontal_RH: normalizeScore(
      Number(score.Lateral_Orbitofrontal_RH),
    ),
    Postcentral_RH: normalizeScore(Number(score.Postcentral_RH)),
    Precentral_LH: normalizeScore(Number(score.Precentral_LH)),
    Parahippocampal_RH: normalizeScore(Number(score.Parahippocampal_RH)),
    Lingual_LH: normalizeScore(Number(score.Lingual_LH)),
    Caudal_Middle_Frontal_RH: normalizeScore(
      Number(score.Caudal_Middle_Frontal_RH),
    ),
    Pars_Opercularis_RH: normalizeScore(Number(score.Pars_Opercularis_RH)),
    Cuneus_LH: normalizeScore(Number(score.Cuneus_LH)),
    Rostral_Anterior_Cingulate_RH: normalizeScore(
      Number(score.Rostral_Anterior_Cingulate_RH),
    ),
    Transverse_Temporal_RH: normalizeScore(
      Number(score.Transverse_Temporal_RH),
    ),
    Entorhinal_LH: normalizeScore(Number(score.Entorhinal_LH)),
    Frontal_Pole_LH: normalizeScore(Number(score.Frontal_Pole_LH)),
    Precuneus_LH: normalizeScore(Number(score.Precuneus_LH)),
    Middle_Temporal_RH: normalizeScore(Number(score.Middle_Temporal_RH)),
    Caudal_Anterior_Cingulate_LH: normalizeScore(
      Number(score.Caudal_Anterior_Cingulate_LH),
    ),
    Pericalcarine_LH: normalizeScore(Number(score.Pericalcarine_LH)),
    Lateral_Occipital_RH: normalizeScore(Number(score.Lateral_Occipital_RH)),
    Fusiform_RH: normalizeScore(Number(score.Fusiform_RH)),
    Pars_Orbitalis_LH: normalizeScore(Number(score.Pars_Orbitalis_LH)),
    Lateral_Orbitofrontal_LH: normalizeScore(
      Number(score.Lateral_Orbitofrontal_LH),
    ),
    Paracentral_LH: normalizeScore(Number(score.Paracentral_LH)),
    Medial_Orbitofrontal_LH: normalizeScore(
      Number(score.Medial_Orbitofrontal_LH),
    ),
    Inferior_Temporal_RH: normalizeScore(Number(score.Inferior_Temporal_RH)),
    Superior_Temporal_RH: normalizeScore(Number(score.Superior_Temporal_RH)),
    Superior_Parietal_LH: normalizeScore(Number(score.Superior_Parietal_LH)),
    Banks_of_Superior_Temporal_Sulcus_LH: normalizeScore(
      Number(score.Banks_of_Superior_Temporal_Sulcus_LH),
    ),
    Inferior_Parietal_LH: normalizeScore(Number(score.Inferior_Parietal_LH)),
    Temporal_Pole_RH: normalizeScore(Number(score.Temporal_Pole_RH)),
    Insula_RH: normalizeScore(Number(score.Insula_RH)),
    Posterior_Cingulate_RH: normalizeScore(
      Number(score.Posterior_Cingulate_RH),
    ),
    Superior_Frontal_RH: normalizeScore(Number(score.Superior_Frontal_RH)),
    Precentral_RH: normalizeScore(Number(score.Precentral_RH)),
    Rostral_Middle_Frontal_RH: normalizeScore(
      Number(score.Rostral_Middle_Frontal_RH),
    ),
    Parahippocampal_LH: normalizeScore(Number(score.Parahippocampal_LH)),
    Pars_Opercularis_LH: normalizeScore(Number(score.Pars_Opercularis_LH)),
    Pars_Triangularis_LH: normalizeScore(Number(score.Pars_Triangularis_LH)),
    Postcentral_LH: normalizeScore(Number(score.Postcentral_LH)),
    Fusiform_LH: normalizeScore(Number(score.Fusiform_LH)),
    Supramarginal_RH: normalizeScore(Number(score.Supramarginal_RH)),
    Precuneus_RH: normalizeScore(Number(score.Precuneus_RH)),
    Transverse_Temporal_LH: normalizeScore(
      Number(score.Transverse_Temporal_LH),
    ),
    Caudal_Middle_Frontal_LH: normalizeScore(
      Number(score.Caudal_Middle_Frontal_LH),
    ),
    Rostral_Anterior_Cingulate_LH: normalizeScore(
      Number(score.Rostral_Anterior_Cingulate_LH),
    ),
    Pericalcarine_RH: normalizeScore(Number(score.Pericalcarine_RH)),
    Caudal_Anterior_Cingulate_RH: normalizeScore(
      Number(score.Caudal_Anterior_Cingulate_RH),
    ),
    Cuneus_RH: normalizeScore(Number(score.Cuneus_RH)),
    Middle_Temporal_LH: normalizeScore(Number(score.Middle_Temporal_LH)),
    Entorhinal_RH: normalizeScore(Number(score.Entorhinal_RH)),
    Pars_Orbitalis_RH: normalizeScore(Number(score.Pars_Orbitalis_RH)),
    Lingual_RH: normalizeScore(Number(score.Lingual_RH)),
    Frontal_Pole_RH: normalizeScore(Number(score.Frontal_Pole_RH)),
    Lateral_Occipital_LH: normalizeScore(Number(score.Lateral_Occipital_LH)),
    Medial_Orbitofrontal_RH: normalizeScore(
      Number(score.Medial_Orbitofrontal_RH),
    ),
  }
}

const HealthCenterReport = () => {
  const {t, i18n} = useTranslation()
  const {textHealthAPI} = useLanguage('health')
  const classes = useStyles()
  const [currentLocale, setCurrentLocale] = useState<Locale>('ko')
  const [tabActive, setTabActive] = useState(0)
  const [listTab, setListTab] = useState<number[]>([])
  const [loadingDownload, setLoadingDownload] = useState(false)
  const {listPackage, getPackageList} = useProductManagement()
  const history = useHistory()
  const {id} = useParams<{id?: string}>()
  const initDataHealthReport = {
    agingContourImage: '',
    agingLobeScore: {
      Frontal_LH: 0,
      Frontal_RH: 0,
      Occipital_LH: 0,
      Occipital_RH: 0,
      Parietal_LH: 0,
      Parietal_RH: 0,
      Temporal_LH: 0,
      Temporal_RH: 0,
    },
    agingRoiScore: {},
    agingThreeDimensionImage: '',
    agingTotalScore: 0,
    analysisTypeId: '0',
    brainConnectivityBetweenLobesImage: '',
    brainConnectivityBetweenLobesScore: null,
    brainConnectivityByLobeImage: '',
    brainConnectivityByLobeScore: null,
    brainConnectivityTotalImage: '',
    brainConnectivityTotalScore: 0,
    choice: 'N',
    createdAt: new Date().toISOString(),
    ecId: 0,
    eoId: 0,
    eoMeasuredTime: 0,
    ecMeasuredTime: 0,
    hrvMeasuredTime: 0,
    fiveFeaturesRadarImage: '',
    fiveFeaturesScore: {
      sadness: 0,
      tension: 0,
      stress: 0,
      dominant_frequency: {
        score: 0,
        value: 0,
      },
      inattention: {score: 0, value: 0},
    },
    hcId: 0,
    hrvAutoNervousSystemBalance: {parasympathetic: 0, sympathetic: 0},
    hrvCircularImage: '',
    hrvContourImage: '',
    hrvHistogramImage: '',
    hrvId: null,
    hrvTableImage: '',
    layoutType: 'C1',
    mciProbability: 0,
    mciResultImage: '',
    measureId: 0,
    patientBirth: '',
    patientFirstName: '',
    patientGender: '',
    patientHand: '',
    patientLastName: '',
    summaryType: 'S',
    hrvContourLevel: 1,
    hrvCircularLevel: 1,
    hrvHistogramLevel: 1,
    finalReportPath: null,
    treatmentAt: new Date().toISOString().split('T')[0],
  }

  const [dataHealthReport, setDataHealthReport] =
    useState<HealthReport>(initDataHealthReport)

  const [dataHealthCheckupReport, setDataHealthCheckupReport] =
    useState<HealthyCheckupReport>({
      final_report_path: '',
      measured_time: {
        eeg_ec: 0,
        eeg_eo: 0,
        hrv: 0,
      },
      images: {
        contour: '',
        hrv_circular: '',
        hrv_contour: '',
        hrv_histogram: '',
        hrv_table: '',
        mci_aging_curve: '',
        radar_chart: '',
        three_dim: '',
        total_connection: '',
      },
      scores: {
        brain_connectivity: {
          inter: {
            horizontal: 0,
            vertical: {
              left: 0,
              right: 0,
            },
          },
          intra: {
            frontal: {
              left: 0,
              right: 0,
            },
            pariocci: {
              left: 0,
              right: 0,
            },
          },
          total: 0,
        },
        brain_mapping_advanced: {
          Rostral_Middle_Frontal_LH: '',
          Paracentral_RH: '',
          Superior_Parietal_RH: '',
          Inferior_Parietal_RH: '',
          Insula_LH: '',
          Posterior_Cingulate_LH: '',
          Superior_Temporal_LH: '',
          Temporal_Pole_LH: '',
          Inferior_Temporal_LH: '',
          Superior_Frontal_LH: '',
          Supramarginal_LH: '',
          Pars_Triangularis_RH: '',
          Banks_of_Superior_Temporal_Sulcus_RH: '',
          Lateral_Orbitofrontal_RH: '',
          Postcentral_RH: '',
          Precentral_LH: '',
          Parahippocampal_RH: '',
          Lingual_LH: '',
          Caudal_Middle_Frontal_RH: '',
          Pars_Opercularis_RH: '',
          Cuneus_LH: '',
          Rostral_Anterior_Cingulate_RH: '',
          Transverse_Temporal_RH: '',
          Entorhinal_LH: '',
          Frontal_Pole_LH: '',
          Precuneus_LH: '',
          Middle_Temporal_RH: '',
          Caudal_Anterior_Cingulate_LH: '',
          Pericalcarine_LH: '',
          Lateral_Occipital_RH: '',
          Fusiform_RH: '',
          Pars_Orbitalis_LH: '',
          Lateral_Orbitofrontal_LH: '',
          Paracentral_LH: '',
          Medial_Orbitofrontal_LH: '',
          Inferior_Temporal_RH: '',
          Superior_Temporal_RH: '',
          Superior_Parietal_LH: '',
          Banks_of_Superior_Temporal_Sulcus_LH: '',
          Inferior_Parietal_LH: '',
          Temporal_Pole_RH: '',
          Insula_RH: '',
          Posterior_Cingulate_RH: '',
          Superior_Frontal_RH: '',
          Precentral_RH: '',
          Rostral_Middle_Frontal_RH: '',
          Parahippocampal_LH: '',
          Pars_Opercularis_LH: '',
          Pars_Triangularis_LH: '',
          Postcentral_LH: '',
          Fusiform_LH: '',
          Supramarginal_RH: '',
          Precuneus_RH: '',
          Transverse_Temporal_LH: '',
          Caudal_Middle_Frontal_LH: '',
          Rostral_Anterior_Cingulate_LH: '',
          Pericalcarine_RH: '',
          Caudal_Anterior_Cingulate_RH: '',
          Cuneus_RH: '',
          Middle_Temporal_LH: '',
          Entorhinal_RH: '',
          Pars_Orbitalis_RH: '',
          Lingual_RH: '',
          Frontal_Pole_RH: '',
          Lateral_Occipital_LH: '',
          Medial_Orbitofrontal_RH: '',
        },
        brain_mapping_basic: {
          lobe_score: {
            Frontal_LH: 0,
            Frontal_RH: 0,
            Occipital_LH: 0,
            Occipital_RH: 0,
            Parietal_LH: 0,
            Parietal_RH: 0,
            Temporal_LH: 0,
            Temporal_RH: 0,
          },
          total_score: 0,
        },
        brain_top_indicators: {
          dominant_frequency: {
            score: 0,
            value: 0,
          },
          inattention: {
            score: 0,
            value: 0,
          },
          sadness: 0,
          stress: 0,
          tension: 0,
        },
        dementia_prediction: 0,
        hrv_analysis: {
          circular_level: 0,
          contour_level: 0,
          histogram_level: 0,
          auto_nervous_system_balance: {
            parasympathetic: 0,
            sympathetic: 0,
          },
        },
      },
      version: 1,
    })

  const {isLocalLanguage} = useLanguageReport(
    'health',
    dataHealthCheckupReport.version,
  )

  const [countChangeLang, setCountChangeLang] = useState<number>(0)

  const [TAB, setTAB] = useState<TabType[]>([
    {index: 0, name: t('I5MajorIndicator')},
    {index: 1, name: t('IBrainFunctionBasic')},
    {index: 2, name: t('IBrainFunctionAdvance')},
    {index: 3, name: t('IBrainConnectivity')},
    {index: 4, name: t('IAIDementiaPrediction')},
    {index: 5, name: t('IHeartRate')},
  ])

  const baseLobeScores = {
    Frontal_LH: 0,
    Frontal_RH: 0,
    Occipital_LH: 0,
    Occipital_RH: 0,
    Parietal_LH: 0,
    Parietal_RH: 0,
    Temporal_LH: 0,
    Temporal_RH: 0,
  }

  const titleHeader = useMemo(() => {
    if (textHealthAPI?.header) {
      return textHealthAPI?.header
    }
    return null
  }, [textHealthAPI])

  const textBrainConnectivity = useMemo(() => {
    if (textHealthAPI?.brain_connectivity) {
      return textHealthAPI?.brain_connectivity
    }
    return null
  }, [textHealthAPI])

  const textMCI = useMemo(() => {
    if (textHealthAPI?.mci) {
      return textHealthAPI?.mci
    }
    return null
  }, [textHealthAPI])

  const textHRV = useMemo(() => {
    if (textHealthAPI?.hrv_analysis) {
      return textHealthAPI?.hrv_analysis
    }
    return null
  }, [textHealthAPI])

  const textHeader = useMemo(() => {
    if (
      dataHealthCheckupReport.measured_time.eeg_eo === null &&
      dataHealthCheckupReport.measured_time.eeg_ec === null
    ) {
      return ''
    }

    return dataHealthCheckupReport.measured_time.eeg_eo === null &&
      dataHealthCheckupReport.measured_time.eeg_ec
      ? t('IContentHeadHealthReportV2', {
          ecMeasuredTime: parseFloat(
            (dataHealthCheckupReport.measured_time.eeg_ec / 60).toFixed(1),
          ),
        })
      : t('IContentHeadHealthReport', {
          ecMeasuredTime: parseFloat(
            (dataHealthCheckupReport.measured_time.eeg_ec / 60).toFixed(1),
          ),
          eoMeasuredTime: parseFloat(
            (dataHealthCheckupReport.measured_time.eeg_eo / 60).toFixed(1),
          ),
        })
  }, [dataHealthCheckupReport, i18n.language])

  const DATA_TITLE = [
    [t('I5MajorIndicator'), textHeader],
    [t('IBrainFunctionBasic'), textHeader],
    [t('IBrainFunctionAdvance'), textHeader],
    [t('IBrainConnectivity'), textHeader],
    [t('IAIDementiaPrediction'), textHeader],
    [t('IHeartRate'), ''],
  ]

  const LANG = [
    {
      label: t('IKoranLang'),
      value: 'ko',
    },
    {
      label: t('IEnglishLang'),
      value: 'en',
    },
    {
      label: t('IChineseLang'),
      value: 'zh',
    },
    // {
    //   label: t('IJapanesesLang'),
    //   value: 'ja',
    // },
    // {
    //   // label: t('ISpanishLang'),
    //   //   label: 'Español',
    //   //   value: 'es',
    // },
  ]

  const handleChangeLang = async (value: Locale) => {
    const count = countChangeLang
    setCountChangeLang(count + 1)
    // setLanguage(value)
    await i18n.changeLanguage(value)
    setCurrentLocale(value)
  }

  const getDataHealthReport = async () => {
    if (Number(id)) {
      try {
        const [responseHealthCheckupReport, responseHealthReport] =
          await Promise.all([
            getDataHealthCheckupReportApi(Number(id), i18n.language),
            getDataHealthReportApi(Number(id), i18n.language),
          ])
        const typeProduct = listPackage?.find(
          (pkg) => pkg.itemId === responseHealthReport.data.analysisTypeId,
        )?.type
        if (typeProduct) {
          const listTab =
            LIST_HEALTH_PRODUCT_TAB.find(
              (product) => typeProduct === product.type,
            )?.tabs || []
          setListTab(listTab)
          if (listTab.length && countChangeLang === 0) setTabActive(listTab[0])
        }

        setDataHealthReport(responseHealthReport.data)
        setDataHealthCheckupReport(responseHealthCheckupReport)
      } catch (error) {
        if (error.err.code === '404')
          history.push(RouteConstant.ERROR_NOT_FOUND.path)
      }
    }
  }

  const handleClickDownLoad = async () => {
    try {
      setLoadingDownload(true)
      const createResponse = await createHealthCheckupPDFReport(
        [Number(id)],
        i18n.language,
      )
      await downloadFileFromWsUrl(createResponse.ws_url, getAccessToken() ?? '')
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingDownload(false)
    }
  }

  const getTitleHeader = (): string => {
    if (!isLocalLanguage && textHealthAPI) {
      if (tabActive === 0) {
        return textHealthAPI?.five_major_features.title
      }
      if (tabActive === 1) {
        return textHealthAPI?.brain_mapping.basic.title
      }
      if (tabActive === 2) {
        return textHealthAPI?.brain_mapping.advanced.title
      }
      if (tabActive === 3) {
        return textHealthAPI?.brain_connectivity.title.toString()
      }
      if (tabActive === 4) {
        return textHealthAPI?.mci.title
      }
      if (tabActive === 5) {
        return textHealthAPI?.hrv_analysis.title
      }
    }
    return ''
  }

  useEffect(() => {
    if (textHealthAPI?.header) {
      if (!isLocalLanguage) {
        const listTab: TabType[] = [
          {
            index: 0,
            name: textHealthAPI?.five_major_features.title,
          },
          {
            index: 1,
            name: textHealthAPI?.brain_mapping.basic.title,
          },
          {
            index: 2,
            name: textHealthAPI?.brain_mapping.advanced.title,
          },
          {
            index: 3,
            name: textHealthAPI?.brain_connectivity.title.toString(),
          },
          {index: 4, name: textHealthAPI?.mci.title},
          {index: 5, name: textHealthAPI?.hrv_analysis.title},
        ]
        setTAB(listTab)
      } else {
        setTAB([
          {index: 0, name: t('I5MajorIndicator')},
          {index: 1, name: t('IBrainFunctionBasic')},
          {index: 2, name: t('IBrainFunctionAdvance')},
          {index: 3, name: t('IBrainConnectivity')},
          {index: 4, name: t('IAIDementiaPrediction')},
          {index: 5, name: t('IHeartRate')},
        ])
      }
    }
  }, [textHealthAPI, i18n.language])

  const isVersion2 = useMemo(() => {
    return dataHealthCheckupReport.version === 2
  }, [dataHealthCheckupReport.version])

  useEffect(() => {
    setCurrentLocale(i18n.language === 'ja' ? 'ko' : (i18n.language as Locale))
  }, [i18n.language])

  useEffect(() => {
    getPackageList({
      target: TYPE_INDICATOR_PRODUCT.HEALTH,
    })
  }, [])

  useEffect(() => {
    if (listPackage?.length) {
      getDataHealthReport()
    }
  }, [i18n.language, listPackage])

  return (
    <div className={classes.root}>
      <div className={classes.topRoot}>
        <div className={`${classes.topNav} no-print`}>
          <div className={classes.topNavLeft}>
            <div className={classes.logo}>
              <img src={Logo} alt='Logo' />
            </div>
            <div className={classes.tabs}>
              {TAB.filter((item) => listTab.includes(item.index)).map((tab) => (
                <div
                  className={`${classes.tab} ${
                    tabActive === tab.index && classes.tabActive
                  }`}
                  onClick={() => setTabActive(tab.index)}
                  aria-hidden='true'
                  key={tab.index}
                >
                  {t(tab.name)}
                </div>
              ))}
            </div>
          </div>
          <div className={classes.topNavRight}>
            <div>
              <Select
                value={currentLocale}
                onChange={(e) => handleChangeLang(e.target.value as Locale)}
                className={classes.searchSelect}
                input={<OutlinedInput name='age' id='outlined-age-simple' />}
              >
                {LANG.map((item) => (
                  <MenuItem value={item.value} key={item.value}>
                    {t(item.label)}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <Button disabled={loadingDownload} onClick={handleClickDownLoad}>
              <img src={IconDownload} alt='IconDownload' />
            </Button>
          </div>
        </div>
        <div id='health-center' className='no-print'>
          {isLocalLanguage ? (
            <Header
              title={DATA_TITLE[tabActive][0]}
              content={tabActive === 5 ? '' : textHeader}
              name={t('IGetFullName', {
                firstName: dataHealthReport.patientFirstName ?? '',
                lastName: dataHealthReport.patientLastName ?? '',
              })}
              sex={t(
                toGenderString(dataHealthReport.patientGender as GenderValue),
              )}
              birthday={dataHealthReport.patientBirth.replaceAll('-', '.')}
              measurementDate={
                dataHealthReport.displayMeasurementDateTime ??
                dataHealthReport.treatmentAt
              }
            />
          ) : (
            <HeaderMultiLangReport
              title={getTitleHeader()}
              tabIndex={tabActive}
              content={tabActive === 5 ? '' : textHeader}
              name={t('IGetFullName', {
                firstName: dataHealthReport.patientFirstName ?? '',
                lastName: dataHealthReport.patientLastName ?? '',
              })}
              sex={t(
                toGenderString(dataHealthReport.patientGender as GenderValue),
              )}
              birthday={dataHealthReport.patientBirth}
              measurementDate={
                dataHealthReport.displayMeasurementDateTime ??
                dataHealthReport.treatmentAt
              }
              titleHeader={titleHeader}
            />
          )}

          {tabActive === 0 &&
            (dataHealthCheckupReport?.scores?.brain_top_indicators !== null ? (
              <>
                {isLocalLanguage ? (
                  <HealthCenterMajorIndicators
                    fiveFeaturesRadarImage={
                      dataHealthCheckupReport.images.radar_chart
                    }
                    fiveFeaturesScore={normalizeFiveFeatures(
                      dataHealthCheckupReport.scores.brain_top_indicators,
                    )}
                    scoreAnnotation={
                      textHealthAPI?.common?.score_annotation?.t_score ?? ''
                    }
                    footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                  />
                ) : (
                  <HealthCenterMajorIndicatorsV1
                    fiveFeaturesRadarImage={
                      dataHealthCheckupReport.images.radar_chart
                    }
                    fiveFeaturesScore={normalizeFiveFeatures(
                      dataHealthCheckupReport.scores.brain_top_indicators,
                    )}
                    scoreAnnotation={
                      textHealthAPI?.common?.score_annotation?.t_score ?? ''
                    }
                    footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                  />
                )}
              </>
            ) : (
              <div className={classes.hrvError}>
                {t('IFiveBrainFunctionNull')}
              </div>
            ))}

          {tabActive === 1 &&
            (dataHealthCheckupReport?.scores?.brain_mapping_basic
              ?.lobe_score !== null ? (
              <>
                {isLocalLanguage ? (
                  <BrainFunctionBasic
                    agingThreeDimensionImage={
                      dataHealthCheckupReport.images.three_dim
                    }
                    agingContourImage={dataHealthCheckupReport.images.contour}
                    agingLobeScore={normalizeBrainMappingBasicByLobeScore(
                      dataHealthCheckupReport?.scores?.brain_mapping_basic
                        ?.lobe_score ?? baseLobeScores,
                    )}
                    footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                  />
                ) : (
                  <BrainFunctionBasicV1
                    agingThreeDimensionImage={
                      dataHealthCheckupReport.images.three_dim
                    }
                    agingContourImage={dataHealthCheckupReport.images.contour}
                    agingLobeScore={normalizeBrainMappingBasicByLobeScore(
                      dataHealthCheckupReport?.scores?.brain_mapping_basic
                        ?.lobe_score ?? baseLobeScores,
                    )}
                    footer={textHealthAPI?.common.footer?.[0] ?? ''}
                  />
                )}
              </>
            ) : (
              <div className={classes.hrvError}>
                {t('IBrainFunctionBasicMappingNull')}
              </div>
            ))}

          {tabActive === 2 &&
            (dataHealthCheckupReport?.scores?.brain_mapping_advanced !==
            null ? (
              <BrainFunctionMappingPageV1
                agingLobeScore={normalizeBrainMappingBasicByLobeScore(
                  dataHealthCheckupReport?.scores?.brain_mapping_basic
                    ?.lobe_score !== null
                    ? dataHealthCheckupReport.scores.brain_mapping_basic
                        .lobe_score
                    : baseLobeScores,
                )}
                agingRoiScore={normalizeBrainMappingAdvancedByRoiScore(
                  dataHealthCheckupReport.scores.brain_mapping_advanced,
                )}
              />
            ) : (
              <div className={classes.hrvError}>
                {t('IHealthReportTabNull', {tab: t('IBrainFunctionAdvance')})}
              </div>
            ))}

          {tabActive === 3 &&
            (dataHealthCheckupReport?.scores?.brain_connectivity !== null ? (
              <>
                {isLocalLanguage ? (
                  <BrainFunctionalConnectivity
                    partOneImg={dataHealthCheckupReport.images.total_connection}
                    brainConnectivityTotalScore={normalizeScore(
                      dataHealthCheckupReport.scores.brain_connectivity.total,
                    )}
                    brainConnectivityBetweenLobesScore={normalizeBrainConnectivityBetweenLobesScore(
                      dataHealthCheckupReport.scores.brain_connectivity.inter,
                    )}
                    brainConnectivityByLobeScore={normalizeBrainConnectivityByLobeScore(
                      dataHealthCheckupReport.scores.brain_connectivity.intra,
                    )}
                    scoreAnnotation={
                      textHealthAPI?.common?.score_annotation?.t_score ?? ''
                    }
                    footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                  />
                ) : (
                  <BrainFunctionalConnectivityV1
                    partOneImg={dataHealthCheckupReport.images.total_connection}
                    brainConnectivityTotalScore={normalizeScore(
                      dataHealthCheckupReport.scores.brain_connectivity.total,
                    )}
                    brainConnectivityBetweenLobesScore={normalizeBrainConnectivityBetweenLobesScore(
                      dataHealthCheckupReport.scores.brain_connectivity.inter,
                    )}
                    brainConnectivityByLobeScore={normalizeBrainConnectivityByLobeScore(
                      dataHealthCheckupReport.scores.brain_connectivity.intra,
                    )}
                    textBrainConnectivity={
                      textBrainConnectivity as ResBrainConnectivity
                    }
                    isEEG={false}
                    pointUnit={textHealthAPI?.common?.score_unit?.t_score ?? ''}
                    scoreAnnotation={
                      textHealthAPI?.common?.score_annotation?.t_score ?? ''
                    }
                    footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                  />
                )}
              </>
            ) : (
              <div className={classes.hrvError}>
                {t('IHealthReportTabNull', {tab: t('IBrainConnectivity')})}
              </div>
            ))}

          {tabActive === 4 &&
            (dataHealthCheckupReport?.scores?.dementia_prediction !== null ? (
              <>
                {isLocalLanguage && !isVersion2 && (
                  <AIDementiaPrediction
                    mciResultImage={
                      dataHealthCheckupReport.images.mci_aging_curve
                    }
                    mciProbability={
                      dataHealthCheckupReport.scores.dementia_prediction
                    }
                    name={t('IGetFullName', {
                      firstName: dataHealthReport.patientFirstName ?? '',
                      lastName: dataHealthReport.patientLastName ?? '',
                    })}
                    footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                  />
                )}
                {isVersion2 && (
                  <AIDementiaPredictionV2
                    mciResultImage={
                      dataHealthCheckupReport.images.mci_aging_curve
                    }
                    mciProbability={
                      dataHealthCheckupReport.scores.dementia_prediction
                    }
                    name={t('IGetFullName', {
                      firstName: dataHealthReport.patientFirstName ?? '',
                      lastName: dataHealthReport.patientLastName ?? '',
                    })}
                    textMCI={textMCI as ResMCIV2}
                    footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                  />
                )}
                {!isLocalLanguage && !isVersion2 && (
                  <AIDementiaPredictionV1
                    mciResultImage={
                      dataHealthCheckupReport.images.mci_aging_curve
                    }
                    mciProbability={
                      dataHealthCheckupReport.scores.dementia_prediction
                    }
                    name={t('IGetFullName', {
                      firstName: dataHealthReport.patientFirstName ?? '',
                      lastName: dataHealthReport.patientLastName ?? '',
                    })}
                    textMCI={textMCI as ResMCI}
                    footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                  />
                )}
              </>
            ) : (
              <div className={classes.hrvError}>
                {t('IHealthReportTabNull', {tab: t('IAIDementiaPrediction')})}
              </div>
            ))}

          {tabActive === 5 &&
            (dataHealthCheckupReport?.scores?.hrv_analysis !== null ? (
              <>
                {isLocalLanguage ? (
                  <HealthCenterHeartRateAnalysisPage
                    hrvTableImage={dataHealthCheckupReport.images.hrv_table}
                    hrvCircularImage={
                      dataHealthCheckupReport.images.hrv_circular
                    }
                    hrvAutoNervousSystemBalance={
                      dataHealthCheckupReport.scores.hrv_analysis
                        .auto_nervous_system_balance
                    }
                    hrvContourImage={dataHealthCheckupReport.images.hrv_contour}
                    hrvHistogramImage={
                      dataHealthCheckupReport.images.hrv_histogram
                    }
                    hrvCircularLevel={
                      dataHealthCheckupReport.scores.hrv_analysis.circular_level
                    }
                    hrvContourLevel={
                      dataHealthCheckupReport.scores.hrv_analysis.contour_level
                    }
                    hrvHistogramLevel={
                      dataHealthCheckupReport.scores.hrv_analysis
                        .histogram_level
                    }
                    footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                  />
                ) : (
                  <HealthCenterHeartRateAnalysisPageV1
                    hrvTableImage={dataHealthCheckupReport.images.hrv_table}
                    hrvCircularImage={
                      dataHealthCheckupReport.images.hrv_circular
                    }
                    hrvAutoNervousSystemBalance={
                      dataHealthCheckupReport.scores.hrv_analysis
                        .auto_nervous_system_balance
                    }
                    hrvContourImage={dataHealthCheckupReport.images.hrv_contour}
                    hrvHistogramImage={
                      dataHealthCheckupReport.images.hrv_histogram
                    }
                    hrvCircularLevel={
                      dataHealthCheckupReport.scores.hrv_analysis.circular_level
                    }
                    hrvContourLevel={
                      dataHealthCheckupReport.scores.hrv_analysis.contour_level
                    }
                    hrvHistogramLevel={
                      dataHealthCheckupReport.scores.hrv_analysis
                        .histogram_level
                    }
                    textHRV={textHRV as ResHrvAnalysis}
                    footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                  />
                )}
              </>
            ) : (
              <div className={classes.hrvError}>{t('IHrvErrorText')}</div>
            ))}
        </div>
        <WrapPrintPage
          dataHealthReport={dataHealthReport}
          dataHealthCheckupReport={dataHealthCheckupReport}
          listTab={listTab}
          DATA_TITLE={DATA_TITLE}
          isLocalLanguage={isLocalLanguage}
          isVersion2={isVersion2}
        />
      </div>
    </div>
  )
}
export default HealthCenterReport
